/* [ START OF MODULE - VER 3.4 (src/context/SyncContext.jsx) ] */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { getActorName } from '../utils/ticketUtils';
import { ShadowDraftService } from '../utils/ShadowDraftService.js';
import {
  getPendingPhotos, clearQueuedPhoto,
  getPendingNotes,  clearQueuedNote,
  getPendingPhotoCount,
} from '../utils/OfflineCacheService.js';
import { SyncContext } from './useSync';

export const SyncProvider = ({ children }) => {
  const [isOnline,     setIsOnline]     = useState(navigator.onLine);
  const [pendingSyncs, setPendingSyncs] = useState([]);
  const [isSyncing,    setIsSyncing]    = useState(false);
  const [pendingPhotos,setPendingPhotos]= useState(0); // photo queue count for badge
  const isSyncingRef                    = useRef(false);
  // RMI-2026-474: processStatusQueue already guarded itself against
  // re-entrancy (isSyncingRef above) -- processNoteQueue/processPhotoQueue
  // never got the same guard. Both read their queue via an async IndexedDB
  // call before doing any work, so two overlapping invocations (now a real
  // possibility with 3 independent triggers: the 30s poll, the `online`
  // event, and RMI-2026-462's visibilitychange listener -- rapid app
  // backgrounding/foregrounding hits this easily) could both read the same
  // still-queued entry before either cleared it, each uploading/logging it
  // a second time.
  const isNoteSyncingRef                = useRef(false);
  const isPhotoSyncingRef               = useRef(false);

  const refreshQueue = useCallback(() => {
    setPendingSyncs(ShadowDraftService.getPendingSyncs());
    // Refresh photo badge count
    getPendingPhotoCount().then(setPendingPhotos).catch(() => {});
  }, []);

  // ── Sync: status updates (ShadowDraftService / localStorage) ─────────────
  // RMI-2026-475: try/finally around the guard, same pattern as
  // processNoteQueue/processPhotoQueue below (RMI-2026-474) -- without it,
  // anything throwing between the true/false assignments that isn't already
  // caught by the per-item try/catch (e.g. ShadowDraftService itself) would
  // leave isSyncingRef stuck true forever, silently disabling status sync
  // for the rest of the session.
  const processStatusQueue = useCallback(async () => {
    if (!navigator.onLine || isSyncingRef.current) return;
    const queue = ShadowDraftService.getPendingSyncs();
    if (queue.length === 0) return;

    isSyncingRef.current = true;
    setIsSyncing(true);
    try {

      for (const draft of queue) {
        const payload = ShadowDraftService.loadDraft(draft.workOrderId);
        if (!payload) continue;
        try {
          if (payload._is_status_update) {
            let finalPayload = { ...payload };
            delete finalPayload._is_status_update;
            if (finalPayload.local_action_time) {
              const clickTime = new Date(finalPayload.local_action_time);
              if (Date.now() - clickTime.getTime() > 60000) {
                const timeString = clickTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
                const tag = `\n\n[DEADZONE PROTOCOL] System auto-recovery. Tech advanced mission to ${finalPayload.status || 'new status'} while offline at ${timeString}.`;
                finalPayload.notes = finalPayload.notes ? finalPayload.notes + tag : tag.trim();
              }
              delete finalPayload.local_action_time;
            }
            const { error } = await supabase.from('work_orders').update(finalPayload).eq('id', draft.workOrderId);
            if (error) throw error;
          } else {
            const { error } = await supabase.from('work_order_execution').upsert(
              { work_order_id: draft.workOrderId, execution_data: payload, updated_at: new Date().toISOString() },
              { onConflict: 'work_order_id' }
            );
            if (error) throw error;
          }
          ShadowDraftService.clearDraft(draft.workOrderId);
          console.log(`[DEADZONE] Status synced: ${draft.workOrderId}`);
        } catch (err) {
          console.error(`[DEADZONE] Status sync failed: ${draft.workOrderId}`, err);
        }
      }

      setPendingSyncs(ShadowDraftService.getPendingSyncs());
    } finally {
      isSyncingRef.current = false;
      setIsSyncing(false);
    }
  }, []);

  // ── Sync: note queue (IndexedDB) ──────────────────────────────────────────
  // RMI-2026-474: guard set BEFORE the async getPendingNotes() read (not
  // after) -- setting it after would leave a window, between the guard
  // check and the read resolving, where a second overlapping call could
  // slip past the same check and double-process the queue.
  const processNoteQueue = useCallback(async () => {
    if (!navigator.onLine || isNoteSyncingRef.current) return;
    isNoteSyncingRef.current = true;
    try {
      const notes = await getPendingNotes();
      if (notes.length === 0) return;

      for (const entry of notes) {
        try {
          const { error } = await supabase.from('work_orders')
            .update({ notes: entry.notes, updated_at: new Date().toISOString() })
            .eq('id', entry.wo_id);
          if (error) throw error;
          await clearQueuedNote(entry.id);
          console.log(`[DEADZONE] Note synced for WO: ${entry.wo_id}`);
        } catch (err) {
          console.error(`[DEADZONE] Note sync failed: ${entry.wo_id}`, err);
        }
      }
    } finally {
      isNoteSyncingRef.current = false;
    }
  }, []);

  // ── Sync: photo queue (IndexedDB) ─────────────────────────────────────────
  // RMI-2026-462: now writes the same GPS/attribution/review-status fields a
  // direct online upload does (UniversalUploaderModal.jsx), and logs the
  // matching EVIDENCE_UPLOADED activity entry -- a queued photo is no longer
  // a lower-fidelity copy of a directly-uploaded one.
  // RMI-2026-474: same reasoning as processNoteQueue above -- guard set
  // BEFORE the async getPendingPhotos() read so a second overlapping call
  // (e.g. the poll firing right as visibilitychange also fires) can't slip
  // past the check and re-upload/re-log the same still-queued photo.
  const processPhotoQueue = useCallback(async () => {
    if (!navigator.onLine || isPhotoSyncingRef.current) return;
    isPhotoSyncingRef.current = true;
    try {
      const photos = await getPendingPhotos();
      if (photos.length === 0) return;

      // RMI-2026-097 established this sentinel as the fallback for actor_id
      // (system_activity_logs.actor_id is NOT NULL) -- never insert a bare
      // null here even if auth.getUser() fails or an old queued entry predates
      // uploaded_by being recorded.
      const SYSTEM_ACTOR_UUID = '00000000-0000-0000-0000-000000000000';
      const { data: userAuth } = await supabase.auth.getUser().catch(() => ({ data: null }));
      const actorId = userAuth?.user?.id || SYSTEM_ACTOR_UUID;
      const actorName = getActorName(null, userAuth?.user, 'Field User (Queued)');

      for (const entry of photos) {
        try {
          const filePath = `${entry.entity_type}/${entry.entity_id}/${entry.filename}`;

          // Upload blob to Supabase Storage
          const { error: storageErr } = await supabase.storage
            .from('tecc-execution-media')
            .upload(filePath, entry.blob, {
              contentType:  entry.mime_type,
              upsert:       true,
            });
          if (storageErr) throw storageErr;

          // Register in tecc_attachments
          await supabase.from('tecc_attachments').insert([{
            entity_type:   entry.entity_type,
            entity_id:     entry.entity_id,
            bucket_name:   'tecc-execution-media',
            file_path:     filePath,
            file_name:     entry.filename,
            mime_type:     entry.mime_type,
            file_size_kb:  Math.round(entry.blob.size / 1024),
            review_status: entry.review_status || 'PENDING',
            uploaded_by:   entry.uploaded_by || actorId,
            capture_lat:   entry.capture_lat ?? null,
            capture_lng:   entry.capture_lng ?? null,
            gps_status:    entry.gps_status || 'SKIPPED',
            notes:         entry.notes || (entry.entity_type === 'WORK_ORDER'
              ? '[DEADZONE] Uploaded after offline capture' : null),
          }]);

          await supabase.from('system_activity_logs').insert([{
            entity_type: entry.entity_type,
            entity_id:   entry.entity_id,
            action_type: 'EVIDENCE_UPLOADED',
            type:        'EVIDENCE',
            priority_level: 1,
            actor_id:    entry.uploaded_by || actorId,
            actor_name:  actorName,
            details:     `Batch uploaded 1 file(s) — "${entry.notes || 'Queued while offline'}"`,
            payload:     { count: 1, bucket: 'tecc-execution-media', deadzone: true },
          }]).catch(err => console.warn('[DEADZONE] Activity log for synced photo failed (non-fatal):', err.message));

          await clearQueuedPhoto(entry.id);
          console.log(`[DEADZONE] Photo synced: ${entry.filename}`);
        } catch (err) {
          console.error(`[DEADZONE] Photo sync failed: ${entry.filename}`, err);
        }
      }

      // Refresh badge
      setPendingPhotos(await getPendingPhotoCount().catch(() => 0));
    } finally {
      isPhotoSyncingRef.current = false;
    }
  }, []);

  // ── Master sync: run all queues ───────────────────────────────────────────
  const processSyncQueue = useCallback(async () => {
    await processStatusQueue();
    await processNoteQueue();
    await processPhotoQueue();
  }, [processStatusQueue, processNoteQueue, processPhotoQueue]);

  // ── queueStatusUpdate (unchanged API) ────────────────────────────────────
  const queueStatusUpdate = useCallback(async (workOrderId, payload) => {
    const actionTime = new Date().toISOString();
    const offlinePayload = {
      ...payload,
      updated_at:        payload.updated_at || actionTime,
      _is_status_update: true,
      local_action_time: actionTime,
    };

    if (!navigator.onLine) {
      ShadowDraftService.saveDraft(workOrderId, offlinePayload);
      setPendingSyncs(ShadowDraftService.getPendingSyncs());
      return { success: true, queued: true };
    } else {
      try {
        const { error } = await supabase.from('work_orders').update(offlinePayload).eq('id', workOrderId);
        if (error) throw error;
        return { success: true, queued: false };
      } catch (err) {
        console.warn('[DEADZONE] Direct write failed, queuing:', err.message);
        ShadowDraftService.saveDraft(workOrderId, offlinePayload);
        setPendingSyncs(ShadowDraftService.getPendingSyncs());
        return { success: true, queued: true };
      }
    }
  }, []);

  // ── Network sentinel ──────────────────────────────────────────────────────
  useEffect(() => {
    refreshQueue();
    const handleOnline  = () => { setIsOnline(true);  processSyncQueue(); };
    const handleOffline = () => { setIsOnline(false); };
    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);

    // RMI-2026-462: resume immediately on return instead of waiting for the
    // next `online` event or up to 30s of poll lag. Covers the "switched to
    // another app mid-upload, came back" case -- anything that ended up
    // queued (offline, or a network hiccup mid-upload) finishes syncing the
    // moment the tab is foregrounded again, with no user action needed.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && navigator.onLine) processSyncQueue();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    const pollInterval = setInterval(() => {
      refreshQueue();
      if (navigator.onLine) processSyncQueue();
    }, 30000);

    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(pollInterval);
    };
  }, [processSyncQueue, refreshQueue]);

  const forceSync = useCallback(() => {
    refreshQueue();
    processSyncQueue();
  }, [refreshQueue, processSyncQueue]);

  return (
    <SyncContext.Provider value={{
      isOnline, pendingSyncs, isSyncing, pendingPhotos,
      forceSync, refreshQueue, queueStatusUpdate,
      processPhotoQueue, processNoteQueue,
    }}>
      {children}
    </SyncContext.Provider>
  );
};

/*
=========================================
CHANGE MANIFEST
=========================================
VER 3.4 — 2026-10-02 — BUILD-2026.1038 — RMI-2026-657: drop the queue-count logs
- Removed the three "[DEADZONE] Processing N status updates / queued notes / queued
  photos" console.log lines (noise on every reconnect). Kept: the per-item "synced"
  logs, every console.error / console.warn failure log, the "[DEADZONE PROTOCOL]"
  audit tag appended to notes, and the "[DEADZONE] Uploaded after offline capture"
  activity-log text (data, not logging). No behavior change.
  Manifest trimmed to the last 5 entries; the oldest archived in
  docs/INLINE_CHANGELOG_ARCHIVE_3.md.
=========================================
VER 3.3 — 2026-08-16 — RMI-2026-475: processStatusQueue try/finally
- Follow-up audit pass after RMI-2026-474 (same bug class): isSyncingRef
  was set true/false at the top/bottom of the function with no try/finally
  -- anything throwing between those two lines that wasn't already caught
  by the per-item try/catch (e.g. ShadowDraftService itself) would leave
  the ref stuck true forever, silently disabling status-update sync for
  the rest of the session. Wrapped the body in try/finally, matching the
  pattern already applied to processNoteQueue/processPhotoQueue.
=========================================
VER 3.2 — 2026-08-16 — RMI-2026-474: close duplicate-upload race in note/photo queues
- Found during an execution-efficiency audit of the RMI-2026-462 changes.
  processStatusQueue already guarded itself against re-entrancy
  (isSyncingRef), but processNoteQueue/processPhotoQueue never got the
  same guard -- both read their queue via an async IndexedDB call before
  doing any work. processSyncQueue (which runs all three) now has 3
  independent triggers -- the 30s poll, the `online` event, and VER 3.1's
  new visibilitychange listener -- so rapid app backgrounding/foregrounding
  could fire two overlapping calls that both read the same still-queued
  photo/note before either cleared it, each uploading/logging it a second
  time.
- New isNoteSyncingRef/isPhotoSyncingRef, set BEFORE the async
  getPendingNotes()/getPendingPhotos() read (not after -- setting it after
  would leave the exact race window open) with try/finally cleanup so the
  guard can't get stuck true on an unexpected throw.
=========================================
VER 3.1 — 2026-08-15 — RMI-2026-462: full-fidelity photo sync + resume-on-return
- User: need a progress indication for media transfers, and uploads should
  continue if the device switches to another app. Scoped here: bring the
  photo-queue fallback (used when explicitly offline or a network hiccup
  hits mid-upload) up to full fidelity with a direct online upload, and
  resume it immediately when the app is foregrounded again instead of
  waiting on the `online` event or the 30s poll.
- processPhotoQueue() now writes capture_lat/capture_lng/gps_status,
  uploaded_by, and review_status into tecc_attachments (previously a fixed
  subset, always PENDING, no GPS/attribution) -- these fields now flow all
  the way from OfflineCacheService.queuePhoto() (see that file's own
  manifest). Also inserts the matching EVIDENCE_UPLOADED
  system_activity_logs entry after a successful drain, matching what a
  direct online upload already logs (UniversalUploaderModal.jsx) --
  non-fatal if it fails, doesn't block clearing the queue entry.
- New visibilitychange listener alongside the existing `online` event and
  30s poll: the moment the tab/PWA is foregrounded again (visible) while
  online, processSyncQueue() runs right away. Combined with the above, a
  photo that got queued while the user had switched to another app finishes
  syncing automatically, at full fidelity, the instant they come back.
=========================================
VER 3.0 — May 12, 2026
- FEATURE: processNoteQueue() — drains the IndexedDB note_queue on reconnect.
  Notes written offline via OfflineCacheService.queueNote() are applied to
  work_orders as a batch update when online is restored.
- FEATURE: processPhotoQueue() — drains the IndexedDB photo_queue on reconnect.
  Each queued entry holds a raw Blob + metadata. On sync: uploads to
  tecc-execution-media, registers in tecc_attachments, then removes from queue.
- FEATURE: pendingPhotos count exposed on context for header badge display.
- REFACTOR: processSyncQueue() now orchestrates all three queues sequentially.
- REFACTOR: processStatusQueue() extracted from processSyncQueue for clarity.
=========================================
=========================================
*/
